import SubscriptionAPI from '@/api/resources/Subscription.api.ts'
import SwitchField from '@/components/commonComponent/customFields/SwitchField'
import Dropdown from '@/components/commonComponent/Dropwdown.tsx'
import { Button } from '@/components/ui/button.tsx'
import InputField from '@/components/ui/InputField.tsx'
import { Label } from '@/components/ui/label.tsx'
import Loader from '@/components/ui/loader.tsx'
import useDocumentTitle from '@/hooks/use-document-title.ts'
import { addSubscriptionData, updateSubscriptionData } from '@/store/slices/subscriptionSlice'
import { SubscriptionFormInputs } from '@/types/field.js'
import { errorToast, successToast } from '@/utils/alerts.tsx'
import { INTERVAL_OPTIONS, TYPE_OPTIONS } from '@/utils/constant.tsx'
import { useValidationMessages } from '@/utils/messages.tsx'
import { useInputValidator } from '@/utils/validator.tsx'
import { AxiosError } from 'axios'
import { Plus, X } from 'lucide-react'
import React, { useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

const SubscriptionAddEdit = () => {

    const { id } = useParams()
    const navigate = useNavigate()
    useDocumentTitle(id ? 'Update Subscription' : 'Add New Subscription', 'Manage Subscription', '/subscription')
    const [loading, setLoading] = useState<boolean>(false)
    const { register, handleSubmit, setValue, formState: { errors, isSubmitting }, reset, control, watch } = useForm<SubscriptionFormInputs>(
        {
            defaultValues: {
                description: [{ title: '' }],
                status: true,
                is_free_trial: false
            }
        }
    )
    const type = watch('type')
    const is_free_trial = watch('is_free_trial')
    const ValidationMessages = useValidationMessages()
    const InputValidator = useInputValidator()
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'description'
    })
    const dispatch = useDispatch()

    React.useEffect(() => {
        if (id) {
            loadData(id)
        }
    }, [id])

    const loadData = async (id: string) => {
        setLoading(true)
        try {
            const response = await SubscriptionAPI.subscriptionById(id)
            if (response.data.success === 1) {
                const { title, amount, interval, status, sub_title, price_id, seq, description = [], type, number_of_scans, free_trial_scans, number_of_days, is_free_trial } = response.data.data || {}
                reset({
                    title,
                    amount,
                    interval,
                    status,
                    sub_title,
                    price_id,
                    seq,
                    description,
                    type,
                    number_of_scans,
                    free_trial_scans,
                    number_of_days,
                    is_free_trial
                })
            } else {
                if (response.data.message) {
                    errorToast(response.data.message)
                } else {
                    errorToast('Subscription not found!')
                }
            }
        } catch (error: unknown) {
            if (error instanceof AxiosError) {
                errorToast(error.response?.data?.message || 'Error occurred in getting subscription data')
            } else {
                errorToast('Error occurred in getting subscription data')
            }
            navigate('/subscription')
        } finally {
            setLoading(false)
        }
    }
    const addDescriptionField = () => {
        append({ title: '' })
    }

    const removeDescriptionField = (index: number) => {
        if (fields.length > 1) {
            remove(index)
        }
    }

    const onSubmit = async (data: SubscriptionFormInputs) => {
        const payload = {
            ...data,
            description: data.description.filter((i) => i?.title?.trim()) || []
        }
        if (id) {
            try {
                const response = await SubscriptionAPI.subscriptionUpdate({ ...payload, id})
                successToast('Subscription Updated Successfully')
                dispatch(updateSubscriptionData(response.data.data))
                navigate('/subscription', { replace: true })
            } catch (error) {
                if (error instanceof AxiosError) {
                    errorToast(error.response?.data?.message || 'Error occurred in adding subscription')
                } else {
                    errorToast('Error occurred in adding subscription')
                }
            }
        } else {
            try {
                const response = await SubscriptionAPI.subscriptionCreate({ ...data, description: data.description.filter((i) => i?.title?.trim()) || [] })
                successToast('Subscription Created Successfully')
                dispatch(addSubscriptionData(response.data.data))
                navigate('/subscription')
            } catch (error) {
                if (error instanceof AxiosError) {
                    errorToast(error.response?.data?.message || 'Error occurred in adding subscription')
                } else {
                    errorToast('Error occurred in adding subscription')
                }
            }
        }
    }

    if (loading) {
        return <Loader />
    }

    return (
        <div className="flex flex-col">
            <div className="grid auto-rows-min gap-4 md:grid-cols-2 pt-4">
                <form onSubmit={handleSubmit(onSubmit)} className="w-3/3 space-y-4">
                    <div className="grid gap-1">
                        <div className="grid gap-1">
                            <InputField
                                label={'Title'}
                                name="title"
                                register={register('title', InputValidator.title)}
                                type="text"
                                error={errors.title?.message}
                                placeholder={ValidationMessages.title.placeholder}
                                maxLength={64}
                                isRequired
                                className='mb-4'
                            />
                        </div>
                        <div className="grid gap-1">
                            <InputField
                                label={'Sub Title'}
                                name="sub_title"
                                register={register('sub_title')}
                                type="text"
                                placeholder={ValidationMessages.sub_title.placeholder}
                                maxLength={256}
                                className='mb-4'
                            />
                        </div>
                        {id && <div className="grid gap-1">
                            <InputField
                                label={'Stripe Price Id'}
                                name="price_id"
                                register={register('price_id', InputValidator.price_id)}
                                type="text"
                                maxLength={256}
                                disabled={true}
                                className='mb-4'
                            />
                        </div>}
                        <div className="grid gap-1">
                            <InputField
                                label={'Amount'}
                                name="amount"
                                register={register('amount', InputValidator.amount)}
                                type="text"
                                error={errors.amount?.message}
                                placeholder={ValidationMessages.amount.placeholder}
                                disabled={!!id}
                                isRequired
                                className='mb-4'
                            />
                        </div>
                        <div className="grid gap-1">
                            <InputField
                                label={'Number of Scans'}
                                name="number_of_scans"
                                register={register('number_of_scans', InputValidator.number_of_scans)}
                                type="text"
                                error={errors.number_of_scans?.message}
                                placeholder={ValidationMessages.number_of_scans.placeholder}
                                isRequired
                                className='mb-4'
                            />
                        </div>
                        <div className="grid gap-1 mb-4">
                            <Label className='text-sm font-medium mb-1 flex items-center gap-1'>Type<span className='text-red-500'>*</span></Label>
                            <Controller
                                name="type"
                                control={control}
                                rules={{ required: 'Subscription type is required' }}
                                render={({ field }) => (
                                    <Dropdown
                                        name={field.name}
                                        value={field.value}
                                        onChange={(value) => {
                                            field.onChange(value)
                                            if (value === 'one_time') {
                                                setValue('interval', '')
                                                setValue('number_of_days', null)
                                                setValue('free_trial_scans', null)
                                                setValue('is_free_trial', false)
                                            }
                                        }}
                                        className='w-full'
                                        placeholder='Select subscription type'
                                        options={TYPE_OPTIONS}
                                        disabled={!!id}
                                    />
                                )}
                            />
                            {errors?.type?.message && (
                                <p className="mt-1 text-sm text-red-600 font-medium">
                                    {errors?.type?.message}
                                </p>
                            )}
                        </div>
                        {type === 'recurring' && <div className="grid gap-1 mb-4">
                            <Label className='text-sm font-medium mb-1 flex items-center gap-1'>Interval<span className='text-red-500'>*</span></Label>
                            <Controller
                                name="interval"
                                control={control}
                                rules={{ required: 'Interval is required' }}
                                render={({ field }) => (
                                    <Dropdown
                                        name={field.name}
                                        value={field.value}
                                        onChange={(value) => {
                                            field.onChange(value)
                                        }}
                                        className='w-full'
                                        placeholder='Select interval'
                                        options={INTERVAL_OPTIONS}
                                        disabled={!!id}
                                    />
                                )}
                            />
                            {errors?.interval?.message && (
                                <p className="mt-1 text-sm text-red-600 font-medium">
                                    {errors?.interval?.message}
                                </p>
                            )}
                        </div>}
                        <div className="grid gap-1">
                            <InputField
                                label={'Display Order'}
                                name="seq"
                                register={register('seq', InputValidator.seq)}
                                type="text"
                                error={errors.seq?.message}
                                placeholder={ValidationMessages.seq.placeholder}
                                isRequired
                                className='mb-4'
                            />
                        </div>
                        {type === 'recurring' && <div className="grid gap-1">
                            <Controller
                                name="is_free_trial"
                                control={control}
                                render={({ field }) => (
                                    <>
                                        <SwitchField
                                            label='Is Free Trial'
                                            name={field.name}
                                            checked={field.value}
                                            onCheckedChange={(value) => {
                                                field.onChange(value)
                                                if (!value) {
                                                    setValue('free_trial_scans', null)
                                                    setValue('number_of_days', null)
                                                }
                                            }}
                                        />
                                    </>
                                )}
                            />
                            {errors?.is_free_trial?.message && (
                                <p className="mt-1 text-sm text-red-600 font-medium">
                                    {errors?.is_free_trial?.message}
                                </p>
                            )}
                        </div>}
                        {is_free_trial && <>
                            <div className="grid gap-1">
                                <InputField
                                    label={'Daily Free Scans'}
                                    name="free_trial_scans"
                                    register={register('free_trial_scans', InputValidator.free_trial_scans)}
                                    type="text"
                                    placeholder={ValidationMessages.free_trial_scans.placeholder}
                                    className='mb-4'
                                    isRequired
                                    error={errors.free_trial_scans?.message}
                                />
                            </div>
                            <div className='grid gap-1'>
                                <InputField
                                    label={'Trial Duration (Days)'}
                                    name="number_of_days"
                                    register={register('number_of_days', InputValidator.number_of_days)}
                                    type="text"
                                    placeholder={ValidationMessages.number_of_days.placeholder}
                                    className='mb-4'
                                    isRequired
                                    error={errors.number_of_days?.message}
                                />
                            </div>
                        </>
                        }
                        <div className="grid">
                            <div className="flex items-center justify-between mb-2">
                                <Label className='text-sm font-medium mb-1 flex items-center gap-1'>Description</Label>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={addDescriptionField}
                                    className="flex items-center gap-2"
                                >
                                    <Plus className="h-4 w-4" />
                                    Add Description
                                </Button>
                            </div>
                            {fields.map((field, index) => (
                                <div key={field.id} className='flex items-center gap-1'>
                                    <InputField
                                        name={`description.${index}.title`}
                                        register={register(`description.${index}.title`)}
                                        type="tel"
                                        className='w-full flex p-0 m-0'
                                        placeholder={'e.g. Unlimited profile analysis'}
                                    />
                                    {fields.length > 1 && <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        onClick={() => removeDescriptionField(index)}
                                        className='mb-4'
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className='flex justify-between'>
                        <Button variant={'destructive'} type="button" onClick={() => { navigate('/subscription') }}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Please Wait...' : id ? 'Update Subscription' : 'Add Subscription'}
                        </Button>
                    </div>
                </form>
            </div >
        </div >
    )
}

export default SubscriptionAddEdit
